
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tailorsApi } from '../api';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';

export default function ViewAssignment() {
  const { id } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignment();
  }, [id]);

  const fetchAssignment = async () => {
    try {
      const res = await tailorsApi.getAssignmentById(id);
      if (res?.data?.data) {
        setAssignment(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching assignment:', err);
      toast.error(err.response?.data?.message || 'Failed to load assignment details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (isCompleted) => {
    return isCompleted ? (
      <span className="badge badge-done">Completed</span>
    ) : (
      <span className="badge badge-in_progress">In Progress</span>
    );
  };

  // Get measurements from customer
  const renderMeasurements = (measurements) => {
    if (!measurements) return <span className="text-muted">No measurements available</span>;

    return (
      <div className="measurements-grid">
        {Object.entries(measurements).map(([category, values]) => (
          <div key={category} className="measurement-category">
            <h4 className="measurement-category-title">{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
            <div className="measurement-values">
              {Object.entries(values || {}).map(([key, value]) => (
                <div key={key} className="measurement-item">
                  <span className="measurement-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                  <span className="measurement-value">{value || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderItemMeasurements = (measurements, garmentType) => {
    if (!measurements) {
      return <span className="text-muted">No measurements available</span>;
    }

    // Define measurement fields for each garment type
    const garmentMeasurements = {
      Shirt: {
        length: ['length'],
        shoulder: ['shoulder'],
        chest: ['upper_chest', 'middle_chest', 'lower_chest'],
        sleeve: ['sleeve_length', 'sleeve_elbow', 'sleeve_kaf'],
        bicep: ['sleeve_bicep'],
      },
      Pant: {
        length: ['length'],
        waist: ['waist'],
        thigh: ['thigh'],
        leg_opening: ['leg_opening'],

      },

    };

    // Get the measurement configuration for the garment type, default to Shirt if not found
    const config = garmentMeasurements[garmentType] || garmentMeasurements['Shirt'];

    // Helper function to format field name for display
    const formatFieldName = (field) => {
      if (!measurements[field]) {
        return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
      return '';
    };

    return (
      <div className="measurements-grid">
        {Object.entries(config).map(([group, fields]) => (
          <div key={group} className="measurement-row">
            {/* Label (Left side) */}
            <div className="measurement-label">
              {group.charAt(0).toUpperCase() + group.slice(1)}:
            </div>

            {/* Values (Right side) */}
            <div className="measurement-values">
              {fields.map((field) => (
                <span key={field} className="measurement-box">
                  {measurements[field] || formatFieldName(field)}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <p>Assignment not found</p>
          <Link to="/tailors" className="btn btn-primary">Back to Tailors</Link>
        </div>
      </div>
    );
  }

  const { tailor, orderItem, laborCost, assignedAt, completedAt, isCompleted, notes } = assignment;
  const order = orderItem?.order;
  const customer = order?.customer;
  const inventory = orderItem?.inventory;
  const lot = inventory?.lot;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/tailors" className="btn btn-secondary btn-sm">← Back</Link>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>
              Assignment Details
            </h1>
            <span className="mono text-sm" style={{ color: 'var(--accent-light)' }}>
              Order: {order?.orderNumber} | Garment: {orderItem?.garmentType}
            </span>
          </div>
        </div>
        <div>
          {getStatusBadge(isCompleted)}
        </div>
      </div>

      <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Customer Information Card */}
          <div className="card p-2">
            <h3 className="card-title">Customer Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                <span className="info-value font-semibold">{customer?.name || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">{customer?.phone || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{customer?.email || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Address</span>
                <span className="info-value">{customer?.address || '—'}</span>
              </div>
            </div>
          </div>

          {/* Order Item Measurements Card */}
          <div className="card p-2">
            <h3 className="card-title">{orderItem?.garmentType} Measurements</h3>
            {renderItemMeasurements(orderItem?.measurements, orderItem?.garmentType)}
          </div>

          {/* Tailor Information Card */}
          <div className="card p-2">
            <h3 className="card-title">Tailor Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                <span className="info-value font-semibold">{tailor?.name || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">{tailor?.phone || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{tailor?.email || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Address</span>
                <span className="info-value">{tailor?.address || '—'}</span>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)' }}>
            <div style={{ marginBottom: '8px', fontWeight: 600, color: 'var(--accent-light)' }}>📱 QR Code</div>
            <div style={{ background: '#fff', padding: '8px', display: 'inline-block', borderRadius: '4px' }}>
              <QRCode
                value={`${window.location.origin}/assignments/${assignment.id}`}
                size={100}
                level={'M'}
                includeMargin={false}
              />
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
              Assignment ID: {assignment.id}
            </p>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Order Details Card */}
          <div className="card p-2">
            <h3 className="card-title">Order Details</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Order Number</span>
                <span className="mono font-semibold" style={{ color: 'var(--accent-light)' }}>{order?.orderNumber || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Garment Type</span>
                <span className="font-semibold">{orderItem?.garmentType || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Order Status</span>
                <span className="badge badge-in_progress">{order?.status || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Item Status</span>
                <span className="badge badge-stitching">{orderItem?.status || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Due Date</span>
                <span>{formatDateShort(order?.dueDate)}</span>
              </div>
              <div className="summary-row">
                <span>Created At</span>
                <span>{formatDateShort(order?.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Cloth & Lot Details Card */}
          <div className="card p-2">
            <h3 className="card-title">Cloth & Lot Details</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Lot Number</span>
                <span className="mono font-semibold">{lot?.lotNumber || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Fabric Type</span>
                <span>{lot?.fabricType || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Color</span>
                <span>{lot?.color || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Cloth Type</span>
                <span>{inventory?.clothType || '—'}</span>
              </div>
              <div className="summary-row total">
                <span>Cloth Given (Meters)</span>
                <span className="font-bold text-warning">
                  {orderItem?.clothQuantityUsed ? `${orderItem.clothQuantityUsed} mtr` : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Cost Details Card */}
          <div className="card p-2">
            <h3 className="card-title">Cost Details</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Cloth Cost</span>
                <span className="font-semibold">₹{Number(orderItem?.clothCost || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="summary-row">
                <span>Labor Cost</span>
                <span className="font-semibold">₹{Number(laborCost || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="summary-row">
                <span>Accessory Cost</span>
                <span className="font-semibold">₹{Number(orderItem?.accessoryCost || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="summary-row total">
                <span>Total Item Cost</span>
                <span className="font-bold text-success">
                  ₹{Number(orderItem?.totalCost || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="summary-row">
                <span>Selling Price</span>
                <span className="font-bold">₹{Number(orderItem?.sellingPrice || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="summary-row">
                <span>Profit</span>
                <span className="font-bold text-success">
                  ₹{Number(orderItem?.profit || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* Assignment Info Card */}
          <div className="card p-2">
            <h3 className="card-title">Assignment Information</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Assigned Date</span>
                <span>{formatDate(assignedAt)}</span>
              </div>
              {completedAt && (
                <div className="summary-row">
                  <span>Completed Date</span>
                  <span>{formatDate(completedAt)}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Status</span>
                {getStatusBadge(isCompleted)}
              </div>
            </div>
            {notes && (
              <div style={{ marginTop: '16px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)' }}>
                <div className="text-xs text-muted" style={{ marginBottom: '4px' }}>Notes</div>
                <div className="text-sm">{notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .grid-2 {
          display: grid;
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .items-center {
          align-items: center;
        }
        .gap-2 {
          gap: 8px;
        }
        .gap-4 {
          gap: 16px;
        }
        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .info-value {
          font-size: 0.9rem;
          color: var(--text-primary);
        }
        .summary-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
        }
        .summary-row:last-child {
          border-bottom: none;
        }
        .summary-row.total {
          font-size: 1rem;
          padding-top: 12px;
          margin-top: 4px;
          border-top: 2px solid var(--border-color);
        }
        
        /* Measurements Styles */
        .measurements-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .measurement-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 6px 0;
        }
        .measurement-row .measurement-label {
          min-width: 80px;
          font-weight: 600;
          color: var(--text-primary);
          font-size: 0.85rem;
          text-transform: capitalize;
        }
        .measurement-row .measurement-values {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .measurement-box {
          display: inline-block;
          padding: 4px 8px;
          background: var(--bg-secondary);
          border-radius: 4px;
          font-size: 0.8rem;
          color: var(--text-primary);
          min-width: 60px;
          text-align: center;
        }
        .measurement-category {
          background: var(--bg-secondary);
          border-radius: var(--radius);
          padding: 12px;
        }
        .measurement-category-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
          text-transform: capitalize;
        }
        .measurement-values {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .measurement-item {
          display: flex;
          justify-content: space-between;
          font-size: 0.8rem;
        }
        .measurement-label {
          color: var(--text-muted);
          text-transform: capitalize;
        }
        .measurement-value {
          font-weight: 500;
          color: var(--text-primary);
        }

        .badge-active { background: #d1fae5; color: #065f46; }
        .badge-inactive { background: #fee2e2; color: #991b1b; }
        .badge-in_progress { background: #dbeafe; color: #1e40af; }
        .badge-done { background: #d1fae5; color: #065f46; }
        .badge-stitching { background: #fef3c7; color: #92400e; }
        .text-warning { color: #f59e0b; }
        .text-success { color: #10b981; }
      `}</style>
    </div>
  );
}
